import { useCallback } from "react";

import { Action, Stack } from "types/generated";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import useSelfServiceFlags from "hooks/useSelfServiceFlags";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";
import useHasStackAdminAccess from "./useHasStackAdminAccess";

const DRIFT_DETECTION_UPDATE_ACCESS_TYPES = [Action.DriftDetectionIntegrationUpdate];

const hasDriftDetectionUpdateAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(DRIFT_DETECTION_UPDATE_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can update drift detection integrations.
 * A user can update drift detection if they have the update permission or if they have admin access to the stack's space.
 * Stacks created from a template deployment (blueprintDeployment) cannot have drift detection updated.
 * @returns {(stack?: Pick<Stack, "spaceDetails" | "space" | "blueprintDeployment">) => AccessResult} Function that takes a stack and returns AccessResult
 */
const useCanUpdateDriftDetection = (): ((
  stack?: Pick<Stack, "spaceDetails" | "space" | "blueprintDeployment">
) => AccessResult) => {
  const hasAdminAccess = useHasStackAdminAccess();
  const { viewer } = useTypedContext(AccountContext);
  const { enableSelfServiceV2 } = useSelfServiceFlags();

  return useCallback(
    (stack) => {
      if (!stack) {
        return { result: false };
      }

      const blueprintCheck = checkTemplateDeploymentAccess(enableSelfServiceV2, stack);
      if (blueprintCheck) {
        return blueprintCheck;
      }

      const hasAccess =
        hasAdminAccess(stack).result ||
        hasDriftDetectionUpdateAccess(viewer, stack.spaceDetails?.id || stack.space);

      if (hasAccess) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [hasAdminAccess, viewer, enableSelfServiceV2]
  );
};

export default useCanUpdateDriftDetection;
